import type { NavigationButton, DividerMenuItem, LinkMenuItem } from '@core/ui/navigation';
import type { PartialDeep } from 'type-fest';
import type { Filter } from 'contexts/preferences/listing-params/types';
import { AVAILABLE_STATUS, NOT_AVAILABLE_SOLD_STATUS } from 'contexts/preferences/listing-params';

// Extended LinkMenuItem with optional filter property
type LinkMenuItemWithFilter = LinkMenuItem & {
  filter?: PartialDeep<Filter>;
};

// Reusable filter constants for composition
const PROPERTY_TYPE_FILTERS = {
  HOUSES: {
    homeType: {
      house: true,
      townhouse: false,
      condo: false,
      land: false,
      commercial: false,
      farm: false,
    },
  },
  CONDOS: {
    homeType: {
      house: false,
      townhouse: false,
      condo: true,
      land: false,
      commercial: false,
      farm: false,
    },
  },
  TOWNHOUSES: {
    homeType: {
      house: false,
      townhouse: true,
      condo: false,
      land: false,
      commercial: false,
      farm: false,
    },
  },
} as const;

const LISTING_STATUS_FILTERS = {
  FOR_SALE: {
    status: AVAILABLE_STATUS,
  },
  RECENTLY_SOLD: {
    status: NOT_AVAILABLE_SOLD_STATUS,
  },
} as const;

const RENTAL_FILTERS = {
  RENTAL: {
    rental: true,
  },
  NOT_RENTAL: {
    rental: false,
  },
} as const;

const SPECIAL_FILTERS = {
  OPEN_HOUSES: {
    openHouse: true,
  },
} as const;

/**
 * Fluent builder for creating navigation items.
 * Consolidates divider, link, and filtered link item creation into a single API.
 */
class NavigationItemBuilder {
  private item: Partial<LinkMenuItemWithFilter> = {};

  /**
   * Creates a divider item.
   */
  static divider(): DividerMenuItem {
    return {
      id: `divider-${Math.random().toString(36).substr(2, 9)}`,
      type: 'divider',
    };
  }

  /**
   * Starts building a link item with id and label.
   */
  static link(id: string, label: string): NavigationItemBuilder {
    const builder = new NavigationItemBuilder();
    const hasCityTemplate = label.includes('{city}');
    
    builder.item = {
      id,
      type: 'link',
      ...(hasCityTemplate ? { template: label } : { label }),
      href: '',
    };
    
    return builder;
  }

  /**
   * Sets the href for the link item.
   */
  href(url: string): this {
    this.item.href = url;
    return this;
  }

  /**
   * Marks the link as external.
   */
  external(isExternal: boolean = true): this {
    this.item.external = isExternal;
    return this;
  }

  /**
   * Adds a property type filter.
   */
  withPropertyType(filter: typeof PROPERTY_TYPE_FILTERS[keyof typeof PROPERTY_TYPE_FILTERS]): this {
    if (!this.item.filter) {
      this.item.filter = {};
    }
    this.item.filter = {
      ...this.item.filter,
      ...filter,
    };
    return this;
  }

  /**
   * Adds a listing status filter.
   */
  withStatus(filter: typeof LISTING_STATUS_FILTERS[keyof typeof LISTING_STATUS_FILTERS]): this {
    if (!this.item.filter) {
      this.item.filter = {};
    }
    this.item.filter = {
      ...this.item.filter,
      ...filter,
    };
    return this;
  }

  /**
   * Adds a rental filter.
   */
  withRental(filter: typeof RENTAL_FILTERS[keyof typeof RENTAL_FILTERS]): this {
    if (!this.item.filter) {
      this.item.filter = {};
    }
    this.item.filter = {
      ...this.item.filter,
      ...filter,
    };
    return this;
  }

  /**
   * Adds a special filter (e.g., open houses).
   */
  withSpecial(filter: typeof SPECIAL_FILTERS[keyof typeof SPECIAL_FILTERS]): this {
    if (!this.item.filter) {
      this.item.filter = {};
    }
    this.item.filter = {
      ...this.item.filter,
      ...filter,
    };
    return this;
  }

  /**
   * Adds a custom filter object (for composition with spread syntax).
   */
  withFilter(filter: PartialDeep<Filter>): this {
    if (!this.item.filter) {
      this.item.filter = {};
    }
    this.item.filter = {
      ...this.item.filter,
      ...filter,
    };
    return this;
  }

  /**
   * Builds and returns the final navigation item.
   */
  build(): LinkMenuItemWithFilter {
    return this.item as LinkMenuItemWithFilter;
  }
}

// Convenience aliases for cleaner code
const nav = NavigationItemBuilder;

export const headerNavigationItems: NavigationButton[] = [
  {
    id: 'buy',
    buttonVariant: 'dropdown',
    label: 'Buy',
    items: [
      nav.link('city-real-estate', '{city} Real Estate')
        .withStatus(LISTING_STATUS_FILTERS.FOR_SALE)
        .withRental(RENTAL_FILTERS.NOT_RENTAL)
        .build(),
      nav.link('houses-sale', 'Houses for Sale')
        .withPropertyType(PROPERTY_TYPE_FILTERS.HOUSES)
        .withStatus(LISTING_STATUS_FILTERS.FOR_SALE)
        .withRental(RENTAL_FILTERS.NOT_RENTAL)
        .build(),
      nav.link('condos-sale', 'Condos for Sale')
        .withPropertyType(PROPERTY_TYPE_FILTERS.CONDOS)
        .withStatus(LISTING_STATUS_FILTERS.FOR_SALE)
        .withRental(RENTAL_FILTERS.NOT_RENTAL)
        .build(),
      nav.link('townhouses-sale', 'Townhouses for Sale')
        .withPropertyType(PROPERTY_TYPE_FILTERS.TOWNHOUSES)
        .withStatus(LISTING_STATUS_FILTERS.FOR_SALE)
        .withRental(RENTAL_FILTERS.NOT_RENTAL)
        .build(),
      nav.link('open-houses', 'Open Houses')
        .withSpecial(SPECIAL_FILTERS.OPEN_HOUSES)
        .withStatus(LISTING_STATUS_FILTERS.FOR_SALE)
        .withRental(RENTAL_FILTERS.NOT_RENTAL)
        .build(),
      nav.link('recently-sold', 'Recently Sold')
        .withStatus(LISTING_STATUS_FILTERS.RECENTLY_SOLD)
        .withRental(RENTAL_FILTERS.NOT_RENTAL)
        .build(),
      nav.divider(),
      nav.link('new-listings', 'New Listings')
        .href('/{site-location}-real-estate?sort=-date')
        .build(),
      nav.link('new-construction', 'New Construction')
        .href('/newhomes')
        .build(),
      nav.link('buying-info', 'Information about Buying')
        .href('https://www.zoocasa.com/blog/category/buying-a-home/')
        .external()
        .build(),
    ],
  },
  {
    id: 'rent',
    buttonVariant: 'dropdown',
    label: 'Rent',
    items: [
      nav.link('city-rentals', '{city} Rentals')
        .withStatus(LISTING_STATUS_FILTERS.FOR_SALE)
        .withRental(RENTAL_FILTERS.RENTAL)
        .build(),
      nav.link('houses-rent', 'Houses for Rent')
        .withPropertyType(PROPERTY_TYPE_FILTERS.HOUSES)
        .withStatus(LISTING_STATUS_FILTERS.FOR_SALE)
        .withRental(RENTAL_FILTERS.RENTAL)
        .build(),
      nav.link('condos-rent', 'Condos for Rent')
        .withPropertyType(PROPERTY_TYPE_FILTERS.CONDOS)
        .withStatus(LISTING_STATUS_FILTERS.FOR_SALE)
        .withRental(RENTAL_FILTERS.RENTAL)
        .build(),
      nav.link('townhouses-rent', 'Townhouses for Rent')
        .withPropertyType(PROPERTY_TYPE_FILTERS.TOWNHOUSES)
        .withStatus(LISTING_STATUS_FILTERS.FOR_SALE)
        .withRental(RENTAL_FILTERS.RENTAL)
        .build(),
      nav.divider(),
      nav.link('new-rental-listings', 'New Listings')
        .href('/{site-location}-real-estate/for-rent?sort=-date')
        .build(),
      nav.link('renting-info', 'Information about Renting')
        .href('https://www.zoocasa.com/blog/?s=rent')
        .external()
        .build(),
    ],
  },
  {
    id: 'sell',
    buttonVariant: 'dropdown',
    label: 'Sell',
    items: [
      nav.link('city-recently-sold', '{city} Recently Sold')
        .withStatus(LISTING_STATUS_FILTERS.RECENTLY_SOLD)
        .withRental(RENTAL_FILTERS.NOT_RENTAL)
        .build(),
      nav.link('sell-with-zoocasa', 'Sell with Zoocasa')
        .href('https://zoocasa.ac-page.com/home-sellers-guide')
        .external()
        .build(),
      nav.link('home-appraisal', 'Home Appraisal')
        .href('/home-appraisal')
        .build(),
      nav.divider(),
      nav.link('selling-info', 'Information about Selling')
        .href('https://www.zoocasa.com/blog/category/selling-your-home/')
        .external()
        .build(),
    ],
  },
];
